import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, Building, Shield, Users, Loader2,
  Plus, Pencil, Trash2, Eye, EyeOff, Save, KeyRound, RotateCcw, DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import api, { getConfig, resetDemoData, getShopConfig, updateShopConfig } from "@/lib/api";

const AVAILABLE_PERMISSIONS = [
  { key: "booking", label: "Daily Booking" },
  { key: "schedule", label: "Schedule" },
  { key: "operations", label: "Operations Log" },
  { key: "roster", label: "Roster" },
  { key: "accounting", label: "Accounting" },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");

  // Business Profile State
  const [profile, setProfile] = useState({ shop_name: "", address: "", phone: "", logo_url: "" });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);

  // Demo mode State
  const [demoMode, setDemoMode] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Shop financial config State
  const [shopCfg, setShopCfg] = useState(null);
  const [shopCfgLoading, setShopCfgLoading] = useState(true);
  const [shopCfgSaving, setShopCfgSaving] = useState(false);

  // Password State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Users State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [userForm, setUserForm] = useState({
    username: "", name: "", password: "", permissions: [],
  });

  // Fetch business profile
  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get("/admin/business-profile");
      setProfile(res.data);
    } catch {
      toast.error("Failed to load business profile");
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const cfg = await getConfig();
      setDemoMode(!!cfg.demo_mode);
    } catch {
      setDemoMode(false);
    }
  }, []);

  const fetchShopCfg = useCallback(async () => {
    try {
      const cfg = await getShopConfig();
      setShopCfg({
        ...cfg,
        income_guarantee_tiers: (cfg.income_guarantee_tiers || []).map((t) => ({
          min_hours: String(t.min_hours), guaranteed_amount: String(t.guaranteed_amount),
        })),
        income_guarantee_arrival_tiers: (cfg.income_guarantee_arrival_tiers || []).map((t) => ({
          start_time: t.start_time || "", end_time: t.end_time || "", guaranteed_amount: String(t.guaranteed_amount),
        })),
        income_guarantee_mode: cfg.income_guarantee_mode || "hours",
        booking_incentive_amount: String(cfg.booking_incentive_amount ?? 0),
        super_rate: String(cfg.super_rate ?? 0),
      });
    } catch {
      toast.error("Failed to load financial config");
    } finally {
      setShopCfgLoading(false);
    }
  }, []);

  // Load profile, users and app config on mount (deferred past the sync tick).
  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => {
      if (!active) return;
      fetchProfile();
      fetchUsers();
      fetchConfig();
      fetchShopCfg();
    });
    return () => {
      active = false;
    };
  }, [fetchProfile, fetchUsers, fetchConfig, fetchShopCfg]);

  // Shop config helpers
  const setCfg = (k, v) => setShopCfg((p) => ({ ...p, [k]: v }));
  const addTier = () => setShopCfg((p) => ({ ...p, income_guarantee_tiers: [...p.income_guarantee_tiers, { min_hours: "", guaranteed_amount: "" }] }));
  const updateTier = (idx, field, value) => setShopCfg((p) => ({
    ...p, income_guarantee_tiers: p.income_guarantee_tiers.map((t, i) => i === idx ? { ...t, [field]: value } : t),
  }));
  const removeTier = (idx) => setShopCfg((p) => ({ ...p, income_guarantee_tiers: p.income_guarantee_tiers.filter((_, i) => i !== idx) }));

  const addArrivalTier = () => setShopCfg((p) => ({
    ...p,
    income_guarantee_arrival_tiers: [...(p.income_guarantee_arrival_tiers || []), { start_time: "", end_time: "", guaranteed_amount: "" }],
  }));
  const updateArrivalTier = (idx, field, value) => setShopCfg((p) => ({
    ...p,
    income_guarantee_arrival_tiers: (p.income_guarantee_arrival_tiers || []).map((t, i) => i === idx ? { ...t, [field]: value } : t),
  }));
  const removeArrivalTier = (idx) => setShopCfg((p) => ({
    ...p,
    income_guarantee_arrival_tiers: (p.income_guarantee_arrival_tiers || []).filter((_, i) => i !== idx),
  }));

  const handleSaveShopCfg = async () => {
    setShopCfgSaving(true);
    try {
      const payload = {
        daily_payout_enabled: !!shopCfg.daily_payout_enabled,
        periodic_payout_enabled: !!shopCfg.periodic_payout_enabled,
        income_guarantee_enabled: !!shopCfg.income_guarantee_enabled,
        income_guarantee_mode: shopCfg.income_guarantee_mode || "hours",
        income_guarantee_tiers: shopCfg.income_guarantee_tiers
          .filter((t) => t.min_hours !== "" && t.guaranteed_amount !== "")
          .map((t) => ({ min_hours: parseFloat(t.min_hours) || 0, guaranteed_amount: parseFloat(t.guaranteed_amount) || 0 })),
        income_guarantee_arrival_tiers: (shopCfg.income_guarantee_arrival_tiers || [])
          .filter((t) => t.start_time && t.end_time && t.guaranteed_amount !== "")
          .map((t) => ({ start_time: t.start_time, end_time: t.end_time, guaranteed_amount: parseFloat(t.guaranteed_amount) || 0 })),
        booking_incentive_enabled: !!shopCfg.booking_incentive_enabled,
        booking_incentive_amount: parseFloat(shopCfg.booking_incentive_amount) || 0,
        superannuation_enabled: !!shopCfg.superannuation_enabled,
        super_rate: parseFloat(shopCfg.super_rate) || 0,
        super_responsibility: shopCfg.super_responsibility || "employer",
        carry_over_opening_cash_enabled: shopCfg.carry_over_opening_cash_enabled !== false,
        safe_drop_enabled: shopCfg.safe_drop_enabled !== false,
        expenses_enabled: shopCfg.expenses_enabled !== false,
        other_income_enabled: shopCfg.other_income_enabled !== false,
      };
      await updateShopConfig(payload);
      toast.success("Financial settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save financial settings");
    } finally {
      setShopCfgSaving(false);
    }
  };

  // Reset demo data (DEMO_MODE only)
  const handleResetDemo = async () => {
    setResetting(true);
    try {
      const res = await resetDemoData();
      toast.success(`Demo reset: ${res.staff} staff, ${res.services} services restored`);
      setResetDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset demo data");
    } finally {
      setResetting(false);
    }
  };

  // Save business profile
  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await api.put("/admin/business-profile", profile);
      setProfile(res.data);
      toast.success("Business profile saved");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setProfileSaving(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    setPasswordSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordSaving(false);
    }
  };

  // User CRUD
  const openUserDialog = (u = null) => {
    if (u) {
      setEditingUser(u);
      setUserForm({
        username: u.username, name: u.name,
        password: "", permissions: u.permissions || [],
      });
    } else {
      setEditingUser(null);
      setUserForm({ username: "", name: "", password: "", permissions: [] });
    }
    setUserDialogOpen(true);
  };

  const handleUserSubmit = async () => {
    if (!userForm.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setUserSubmitting(true);
    try {
      if (editingUser) {
        const updateData = { name: userForm.name, permissions: userForm.permissions };
        if (userForm.password) updateData.password = userForm.password;
        await api.put(`/admin/users/${editingUser.id}`, updateData);
        toast.success("User updated");
      } else {
        if (!userForm.username.trim() || !userForm.password) {
          toast.error("Username and password are required");
          setUserSubmitting(false);
          return;
        }
        await api.post("/admin/users", {
          username: userForm.username.trim(),
          name: userForm.name.trim(),
          password: userForm.password,
          role: "staff",
          permissions: userForm.permissions,
        });
        toast.success("User created");
      }
      setUserDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    } finally {
      setUserSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await api.delete(`/admin/users/${deleteUserId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleteUserId(null);
    }
  };

  const togglePermission = (key) => {
    setUserForm(prev => {
      const perms = [...prev.permissions];
      const idx = perms.indexOf(key);
      if (idx >= 0) perms.splice(idx, 1);
      else perms.push(key);
      return { ...prev, permissions: perms };
    });
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64 text-sage">
        <p>Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-settings">
      <div>
        <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" />
          Admin Settings
        </h2>
        <p className="text-sage text-sm">Manage business profile, security, and user accounts</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-sand">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <Building className="h-4 w-4 mr-2" /> Business
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="h-4 w-4 mr-2" /> Security
          </TabsTrigger>
          <TabsTrigger value="financials" data-testid="tab-financials">
            <DollarSign className="h-4 w-4 mr-2" /> Financials
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="h-4 w-4 mr-2" /> Staff Users
          </TabsTrigger>
        </TabsList>

        {/* Business Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-heading text-forest">Business Profile</CardTitle>
              <CardDescription>Update your shop&apos;s display information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profileLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-forest">Shop Name</Label>
                      <Input
                        value={profile.shop_name}
                        onChange={e => setProfile(p => ({ ...p, shop_name: e.target.value }))}
                        placeholder="TherapyM"
                        data-testid="profile-shop-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-forest">Phone</Label>
                      <Input
                        value={profile.phone}
                        onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+61 3 1234 5678"
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-forest">Address</Label>
                    <Input
                      value={profile.address}
                      onChange={e => setProfile(p => ({ ...p, address: e.target.value }))}
                      placeholder="123 Main St, South Melbourne VIC 3205"
                      data-testid="profile-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-forest">Logo URL</Label>
                    <Input
                      value={profile.logo_url}
                      onChange={e => setProfile(p => ({ ...p, logo_url: e.target.value }))}
                      placeholder="https://example.com/logo.png"
                      data-testid="profile-logo-url"
                    />
                  </div>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                    className="bg-forest hover:bg-forest-light"
                    data-testid="save-profile-btn"
                  >
                    {profileSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Profile
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Demo Sandbox — only visible when DEMO_MODE is enabled */}
          {demoMode && (
            <Card className="border-ochre/40 bg-ochre/5 mt-6" data-testid="demo-reset-card">
              <CardHeader>
                <CardTitle className="font-heading text-forest flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-ochre" /> Demo Sandbox
                </CardTitle>
                <CardDescription>
                  This environment is running in demo mode. Reset wipes all bookings, cash flow,
                  payroll and roster data, then restores the default services and staff — giving
                  every prospect a clean, populated example.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="border-ochre text-ochre hover:bg-ochre hover:text-white"
                  onClick={() => setResetDialogOpen(true)}
                  data-testid="reset-demo-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Demo Data
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-heading text-forest flex items-center gap-2">
                <KeyRound className="h-5 w-5" /> Change Admin Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords ? "text" : "password"}
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPasswords(!showPasswords)}
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">New Password</Label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Confirm New Password</Label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={passwordSaving || !currentPassword || !newPassword}
                className="bg-forest hover:bg-forest-light"
                data-testid="change-password-btn"
              >
                {passwordSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Change Password
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials">
          {shopCfgLoading || !shopCfg ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
          ) : (
            <div className="space-y-6" data-testid="financials-tab">
              {/* Staff Payout Methods */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-forest">Staff Payout Methods</CardTitle>
                  <CardDescription>Choose how the shop settles staff. Enable one or both.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between" data-testid="payout-daily-row">
                    <div>
                      <p className="text-sm font-medium text-forest">Daily Payout</p>
                      <p className="text-xs text-sage">Shows the &quot;Daily Staff Cash Payout&quot; panel on the Accounting tab for same-day cash settlement.</p>
                    </div>
                    <Switch
                      checked={!!shopCfg.daily_payout_enabled}
                      onCheckedChange={(v) => setCfg("daily_payout_enabled", v)}
                      data-testid="toggle-daily-payout"
                    />
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-4" data-testid="payout-periodic-row">
                    <div>
                      <p className="text-sm font-medium text-forest">Accumulated Periodic Payout (Weekly / Cycle)</p>
                      <p className="text-xs text-sage">Tracks long-term balances and bulk-settles them in the Admin Payroll console.</p>
                    </div>
                    <Switch
                      checked={!!shopCfg.periodic_payout_enabled}
                      onCheckedChange={(v) => setCfg("periodic_payout_enabled", v)}
                      data-testid="toggle-periodic-payout"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* End-of-Day Workflow */}
              <Card className="border-border" data-testid="eod-workflow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-forest">End-of-Day Workflow</CardTitle>
                  <CardDescription>Turn off any stream you don&apos;t use. Disabled streams hide their Accounting card and count as $0 in the cash check.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: "carry_over_opening_cash_enabled", title: "Carry-over Opening Cash", desc: "Roll yesterday's counted cash into today's opening float." },
                    { key: "safe_drop_enabled", title: "Safe Drop", desc: "Show the Safe cash card for moving drawer cash into the safe." },
                    { key: "expenses_enabled", title: "Daily Expenses", desc: "Show the Daily Expenses(Cash) card." },
                    { key: "other_income_enabled", title: "Other Income", desc: "Show the Other Income (retail) card." },
                  ].map((f) => (
                    <div key={f.key} className="flex items-center justify-between border-b border-border last:border-0 pb-4 last:pb-0" data-testid={`eod-row-${f.key}`}>
                      <div>
                        <p className="text-sm font-medium text-forest">{f.title}</p>
                        <p className="text-xs text-sage">{f.desc}</p>
                      </div>
                      <Switch
                        checked={shopCfg[f.key] !== false}
                        onCheckedChange={(v) => setCfg(f.key, v)}
                        data-testid={`toggle-${f.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Income Guarantee */}
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-forest">Income Guarantee</CardTitle>
                    <CardDescription>Guarantee a minimum daily payout based on hours worked (highest matching tier applies). Set globally; individual staff profiles can override.</CardDescription>
                  </div>
                  <Switch
                    checked={!!shopCfg.income_guarantee_enabled}
                    onCheckedChange={(v) => setCfg("income_guarantee_enabled", v)}
                    data-testid="toggle-income-guarantee"
                  />
                </CardHeader>
                {shopCfg.income_guarantee_enabled && (
                  <CardContent className="space-y-4">
                    <div className="max-w-xs space-y-1">
                      <Label className="text-sm font-medium text-forest">Calculation Mode</Label>
                      <Select value={shopCfg.income_guarantee_mode || "hours"} onValueChange={(v) => setCfg("income_guarantee_mode", v)}>
                        <SelectTrigger data-testid="guarantee-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Total Worked Hours</SelectItem>
                          <SelectItem value="arrival">Arrival Time Tiers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(shopCfg.income_guarantee_mode || "hours") === "hours" ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-forest">Worked-Hours Tiers</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="add-global-tier-btn">
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add Tier
                          </Button>
                        </div>
                        {shopCfg.income_guarantee_tiers.length === 0 && (
                          <p className="text-xs text-sage/70 italic">No tiers — therapists earn straight commission until you add one.</p>
                        )}
                        {shopCfg.income_guarantee_tiers.map((t, idx) => (
                          <div key={idx} className="flex items-center gap-2" data-testid={`global-tier-row-${idx}`}>
                            <span className="text-sm text-sage">Work hours ≥</span>
                            <Input type="number" min="0" step="0.5" value={t.min_hours} placeholder="Hours"
                              onChange={(e) => updateTier(idx, "min_hours", e.target.value)} className="w-24" data-testid={`global-tier-hours-${idx}`} />
                            <span className="text-sm text-sage">⇒ $</span>
                            <Input type="number" min="0" step="1" value={t.guaranteed_amount} placeholder="Payout"
                              onChange={(e) => updateTier(idx, "guaranteed_amount", e.target.value)} className="w-28" data-testid={`global-tier-amount-${idx}`} />
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeTier(idx)} data-testid={`remove-global-tier-${idx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-forest">Arrival-Time Tiers</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addArrivalTier} data-testid="add-arrival-tier-btn">
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add Tier
                          </Button>
                        </div>
                        <p className="text-xs text-sage/70">Ranges must not overlap. Clock-in inside a range earns that payout.</p>
                        {(shopCfg.income_guarantee_arrival_tiers || []).length === 0 && (
                          <p className="text-xs text-sage/70 italic">No arrival tiers yet.</p>
                        )}
                        {(shopCfg.income_guarantee_arrival_tiers || []).map((t, idx) => (
                          <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`arrival-tier-row-${idx}`}>
                            <span className="text-sm text-sage">Clock in</span>
                            <Input type="time" value={t.start_time}
                              onChange={(e) => updateArrivalTier(idx, "start_time", e.target.value)} className="w-28" data-testid={`arrival-tier-start-${idx}`} />
                            <span className="text-sm text-sage">–</span>
                            <Input type="time" value={t.end_time}
                              onChange={(e) => updateArrivalTier(idx, "end_time", e.target.value)} className="w-28" data-testid={`arrival-tier-end-${idx}`} />
                            <span className="text-sm text-sage">⇒ $</span>
                            <Input type="number" min="0" step="1" value={t.guaranteed_amount} placeholder="Payout"
                              onChange={(e) => updateArrivalTier(idx, "guaranteed_amount", e.target.value)} className="w-28" data-testid={`arrival-tier-amount-${idx}`} />
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeArrivalTier(idx)} data-testid={`remove-arrival-tier-${idx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Booking Incentive */}
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-forest">Booking Incentive</CardTitle>
                    <CardDescription>Pay therapists a flat bonus for each session marked as &quot;Booked&quot; (pre-booked, not walk-in).</CardDescription>
                  </div>
                  <Switch
                    checked={!!shopCfg.booking_incentive_enabled}
                    onCheckedChange={(v) => setCfg("booking_incentive_enabled", v)}
                    data-testid="toggle-booking-incentive"
                  />
                </CardHeader>
                {shopCfg.booking_incentive_enabled && (
                  <CardContent className="space-y-2 max-w-xs">
                    <Label className="text-sm font-medium text-forest">Incentive per booked session ($)</Label>
                    <Input type="number" min="0" step="0.5" value={shopCfg.booking_incentive_amount}
                      onChange={(e) => setCfg("booking_incentive_amount", e.target.value)}
                      placeholder="2.00" data-testid="booking-incentive-amount" />
                  </CardContent>
                )}
              </Card>

              {/* Superannuation */}
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-forest">Superannuation</CardTitle>
                    <CardDescription>Calculate super on gross earnings. Shown in the Admin Payroll ledger only.</CardDescription>
                  </div>
                  <Switch
                    checked={!!shopCfg.superannuation_enabled}
                    onCheckedChange={(v) => setCfg("superannuation_enabled", v)}
                    data-testid="toggle-superannuation"
                  />
                </CardHeader>
                {shopCfg.superannuation_enabled && (
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-forest">Superannuation Percentage (%)</Label>
                      <Input type="number" min="0" step="0.1" value={shopCfg.super_rate}
                        onChange={(e) => setCfg("super_rate", e.target.value)}
                        placeholder="11.5" data-testid="super-rate-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-forest">Payment Responsibility</Label>
                      <Select value={shopCfg.super_responsibility} onValueChange={(v) => setCfg("super_responsibility", v)}>
                        <SelectTrigger data-testid="super-responsibility-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employer">Employer Pays Extra</SelectItem>
                          <SelectItem value="deduct">Deduct from Staff Gross Earnings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                )}
              </Card>

              <Button onClick={handleSaveShopCfg} disabled={shopCfgSaving} className="bg-forest hover:bg-forest-light" data-testid="save-financials-btn">
                {shopCfgSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Financial Settings
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Staff Users Tab */}
        <TabsContent value="users">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-forest">Staff User Accounts</CardTitle>
                <CardDescription>Manage login accounts and module permissions</CardDescription>
              </div>
              <Button onClick={() => openUserDialog()} className="bg-forest hover:bg-forest-light" data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
              ) : (
                <Table data-testid="users-table">
                  <TableHeader>
                    <TableRow className="bg-sand">
                      <TableHead className="table-header-cell">Username</TableHead>
                      <TableHead className="table-header-cell">Name</TableHead>
                      <TableHead className="table-header-cell">Role</TableHead>
                      <TableHead className="table-header-cell">Permissions</TableHead>
                      <TableHead className="table-header-cell w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.username}</TableCell>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={u.role === "admin" ? "bg-forest/10 text-forest border-forest/30" : "bg-sage/10 text-sage border-sage/30"}>
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.role === "admin" ? (
                            <span className="text-xs text-sage">Full Access</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(u.permissions || []).map(p => (
                                <Badge key={p} variant="outline" className="text-xs">
                                  {AVAILABLE_PERMISSIONS.find(ap => ap.key === p)?.label || p}
                                </Badge>
                              ))}
                              {(!u.permissions || u.permissions.length === 0) && (
                                <span className="text-xs text-sage">No access</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.role !== "admin" && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openUserDialog(u)} data-testid={`edit-user-${u.id}`}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteUserId(u.id)} data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">
              {editingUser ? "Edit Staff User" : "Add Staff User"}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? "Update account details and permissions" : "Create a new staff login account"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editingUser && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Username *</Label>
                <Input
                  value={userForm.username}
                  onChange={e => setUserForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="e.g. manager, staff_day"
                  data-testid="user-username-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Display Name *</Label>
              <Input
                value={userForm.name}
                onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Day Manager"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">
                {editingUser ? "New Password (leave blank to keep current)" : "Password *"}
              </Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))}
                placeholder={editingUser ? "Leave blank to keep current" : "Set password"}
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <Label className="text-sm font-medium text-forest">Module Permissions</Label>
              <p className="text-xs text-sage">Toggle which pages this user can access</p>
              {AVAILABLE_PERMISSIONS.map(p => (
                <div key={p.key} className="flex items-center justify-between" data-testid={`perm-toggle-${p.key}`}>
                  <span className="text-sm">{p.label}</span>
                  <Switch
                    checked={userForm.permissions.includes(p.key)}
                    onCheckedChange={() => togglePermission(p.key)}
                    data-testid={`perm-switch-${p.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUserSubmit}
              disabled={userSubmitting}
              className="bg-forest hover:bg-forest-light"
              data-testid="save-user-btn"
            >
              {userSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingUser ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this user account.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Demo Data Confirmation */}
      <AlertDialog open={resetDialogOpen} onOpenChange={(o) => !resetting && setResetDialogOpen(o)}>
        <AlertDialogContent data-testid="reset-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all bookings, expenses, cash flow, payroll, roster and safe
              records, then restores the default services and staff. Your admin login and business
              profile are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting} data-testid="cancel-reset-demo">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleResetDemo(); }}
              className="bg-ochre hover:bg-ochre/90"
              data-testid="confirm-reset-demo"
            >
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reset Demo Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
